import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// Billing home (2026-10-25, billing MVP week) - SHADOW and DARK.
// Reachable only when the server's billing gate says yes (lib/billingAccess.js);
// anyone else is sent home as if the page didn't exist.
// PR #2 (2026-10-26): Money In - the EOBs the bot read, newest first. Each opens
// its review page (BillingRemit.jsx). Nothing here changes Raintree.
// PR #4 (2026-10-28): a link to PI liens (BillingPI.jsx).
// PR #6 (2026-10-30): the chain at a glance - a card per stage with its counts and its SHADOW
// SCORE (how often the portal agrees with what staff did in Raintree), from GET /billing/home -
// and the links to every page: PI liens, WC readiness, WC follow-up by carrier.
// PR #72 update (2026-11-01, Caleb's walk): the home is the starting point, not a list.
//   - the Billing bar on top (components/BillingNav.jsx), the same on every billing page
//   - when the Raintree copy last ran, amber when it is over a day old
//   - "Needs attention": one tile per queue, each opening its list (a first pass - Shawna and Cynthia
//     say on the walk what they want to see first each morning)
//   - the chain: every stage card opens its page; card 1 is the nightly copy itself, marked as status
//   - the EOB table moved to its own page, Money In (BillingMoneyIn.jsx, /billing/eob)
// Same single read as before (GET /billing/home); nothing new on the server.

const STALE_HOURS = 30;   // the copy runs every night: over a day and a bit means a night was missed

function money(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function count(v) {
  return Number(v || 0).toLocaleString();
}

function usDay(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function pct(v) {
  return v === null || v === undefined ? "—" : `${v}%`;
}

function Freshness({ ch }) {
  if (ch.error) {
    return (
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="billing-freshness">
        Raintree copy: couldn't read when it last ran.
      </div>
    );
  }
  const t = ch.as_of ? new Date(ch.as_of) : null;
  const hours = t ? (Date.now() - t.getTime()) / 3600000 : NaN;
  const fresh = hours <= STALE_HOURS;
  return (
    <div data-testid="billing-freshness"
         className={`mt-4 rounded-lg border px-4 py-2 text-sm ${fresh ? "border-slate-200 bg-white text-slate-600" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
      {t ? <>Raintree copy refreshed <span className="font-medium">{t.toLocaleString()}</span></> : "Raintree copy: no nightly run recorded yet"}
      {fresh
        ? " · it refreshes every night from that morning's Raintree copy, so what's keyed in Raintree on a Monday shows here on Wednesday morning."
        : t ? " · over a day old: last night's copy may have missed its run, so the numbers below may be behind." : ""}
    </div>
  );
}

function Tile({ to, testid, n, label, sub, calm }) {
  return (
    <Link to={to} data-testid={testid}
          className={`group flex flex-col rounded-xl border bg-white p-4 shadow-sm hover:bg-slate-50 ${calm ? "border-slate-200 hover:border-slate-300" : "border-amber-300 hover:border-amber-400"}`}>
      <span className={`text-2xl font-semibold ${calm ? "text-slate-700" : "text-amber-800"}`}>{n}</span>
      <span className="mt-0.5 text-sm font-medium text-slate-800">{label}</span>
      {sub && <span className="mt-1 text-xs text-slate-500">{sub}</span>}
      <span className="mt-auto pt-3 text-xs font-medium text-sky-700 group-hover:underline">Open the list →</span>
    </Link>
  );
}

function Unavailable() {
  return <div className="text-amber-700">Unavailable right now.</div>;
}

function NeedsAttention({ d }) {
  const nw = d.network || {};
  const pi = d.pi || {};
  const wc = d.wc || {};
  const eob = d.eob || {};
  const tiles = [];
  if (!nw.error) {
    const pending = Number(nw.intake?.pending || 0);
    const failed = Number(nw.intake?.failed || 0);
    const reading = Number(nw.intake?.extracting || 0);
    const bits = [reading ? `${count(reading)} batch${reading === 1 ? "" : "es"} being read` : "", failed ? `${count(failed)} failed to read` : ""].filter(Boolean);
    tiles.push(<Tile key="intake" to="/billing/intake" testid="attention-intake" n={count(pending)} calm={!pending && !failed}
                     label="network lines to review" sub={bits.length ? bits.join(" · ") : "Network clinic batches (HCFAs) waiting for a person"} />);
  }
  if (!eob.error) {
    const proposed = Number(eob.lines?.proposed || 0);
    tiles.push(<Tile key="eob" to="/billing/eob" testid="attention-eob" n={count(proposed)} calm={!proposed}
                     label="EOB lines waiting for approval"
                     sub={`${count(eob.lines?.open)} with no match yet${eob.last_received ? ` · last EOB received ${usDay(eob.last_received)}` : ""}`} />);
  }
  if (!wc.error) {
    const bounce = Number(wc.readiness?.bounce || 0);
    const due = Number(wc.followup?.due || 0);
    tiles.push(<Tile key="wc" to="/billing/wc" testid="attention-wc" n={count(bounce)} calm={!bounce}
                     label="WC claims would bounce or go on paper" sub={`In the last 30 days, of ${count(wc.readiness?.claims)} claims`} />);
    tiles.push(<Tile key="wcf" to="/billing/wc/followup" testid="attention-wc-followup" n={count(due)} calm={!due}
                     label="WC claims due for follow-up" sub={`${money(wc.followup?.due_balance)} unpaid past 21 days e-billed, 45 on paper`} />);
  }
  if (!pi.error) {
    const none = Number(pi.care_complete?.no_attorney || 0);
    tiles.push(<Tile key="pi" to="/billing/pi/no-attorney" testid="attention-pi" n={count(none)} calm={!none}
                     label="PI cases, care complete, no attorney on file" sub="Raintree lists no law firm on these cases" />);
  }
  return (
    <div className="mt-6" data-testid="billing-attention">
      <h2 className="text-lg font-semibold text-slate-800">Needs attention</h2>
      <p className="text-sm text-slate-500">
        The work waiting in each list. Amber means there is something to do. A first pass: tell us what you want to see here first each morning.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">{tiles}</div>
    </div>
  );
}

function StageCard({ n, title, to, testid, children, score }) {
  const body = (
    <>
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">{n}</span>
        <span className="text-sm font-semibold text-slate-800">{title}</span>
      </div>
      <div className="mt-2 space-y-0.5 text-sm text-slate-600">{children}</div>
      {score && <div className="mt-3 rounded-lg bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">{score}</div>}
    </>
  );
  return to ? (
    <Link to={to} data-testid={testid}
          className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 hover:bg-slate-50">
      {body}
      <span className="mt-auto pt-3 text-xs font-medium text-sky-700 group-hover:underline">Open →</span>
    </Link>
  ) : (
    <div data-testid={testid} className="flex flex-col rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
      {body}
      <span className="mt-auto pt-3 text-xs text-slate-500">Status only: the nightly copy every page reads.</span>
    </div>
  );
}

function Chain({ d }) {
  const ch = d.charges || {};
  const nw = d.network || {};
  const pi = d.pi || {};
  const wc = d.wc || {};
  const eob = d.eob || {};
  const rc = ch.recon;
  const es = eob.score;
  return (
    <div className="mt-8" data-testid="billing-chain">
      <h2 className="text-lg font-semibold text-slate-800">The chain</h2>
      <p className="text-sm text-slate-500">From the visit to the posted payment. Each stage's shadow score says how often the portal agrees with what staff did in Raintree.</p>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <StageCard n="1" title="Charges: WestStar clinics" testid="chain-charges"
                   score={ch.score ? `Shadow score: ${pct(ch.score.pct)} of ${count(ch.score.of)} charges agree with their Raintree payment lines (last night)` : undefined}>
          {ch.error ? <Unavailable /> : (
            <>
              <div>{rc ? `${count(rc.charges)} charges from Raintree` : "No reconciliation run yet"}</div>
              <div className="text-xs text-slate-500">Copied from Raintree every night{ch.as_of ? `, last ${usDay(ch.as_of)}` : ""}</div>
            </>
          )}
        </StageCard>
        <StageCard n="2" title="Charges: network clinics" to="/billing/intake" testid="chain-network"
                   score={nw.score ? `Shadow score: ${nw.score.agree} of ${nw.score.of} lines match what staff keyed as reviewed (${pct(nw.score.pct)})${nw.score.at_sister ? `, ${nw.score.at_sister} of them keyed under the clinic's sister location` : ""}${nw.score.as_read && nw.score.as_read.of ? `; as read, ${nw.score.as_read.agree} of ${nw.score.as_read.of} (${pct(nw.score.as_read.pct)})` : ""}` : undefined}>
          {nw.error ? <Unavailable /> : (
            <>
              <div>{count(nw.intake?.items)} proposed lines</div>
              {Number(nw.intake?.items || 0) === 0 && <div className="text-xs text-slate-500">No batch uploaded yet.</div>}
              <div className="text-xs text-slate-500">
                {count(nw.intake?.pending)} to review · {Number(nw.intake?.accepted || 0) + Number(nw.intake?.edited || 0)} accepted · {Number(nw.intake?.rejected || 0)} rejected
              </div>
            </>
          )}
        </StageCard>
        <StageCard n="3" title="PI liens" to="/billing/pi" testid="chain-pi"
                   score={pi.error || !pi.score || !pi.score.of ? undefined : `Shadow score: ${count(pi.score.agree)} of ${count(pi.score.of)} open cases tie to Raintree to the cent (${pct(pi.score.pct)}); ${count(pi.score.charges_off)} charges off`}>
          {pi.error ? <Unavailable /> : (
            <>
              <div>{money(pi.open_balance)} owed on {count(pi.open_cases)} cases</div>
              <div className="text-xs text-slate-500">
                Care complete: {count(pi.care_complete?.cases)} cases, {count(pi.care_complete?.no_attorney)} with no attorney on file
              </div>
            </>
          )}
        </StageCard>
        <StageCard n="4" title="WC claims" to="/billing/wc" testid="chain-wc">
          {wc.error ? <Unavailable /> : (
            <>
              <div>Last 30 days: {count(wc.readiness?.claims)} claims</div>
              <div className="text-xs text-slate-500">
                {count(wc.readiness?.bounce)} would bounce or go on paper · {count(wc.readiness?.review)} review holds
              </div>
              <div className="pt-1">Follow-up: {count(wc.followup?.due)} due ({money(wc.followup?.due_balance)})</div>
            </>
          )}
        </StageCard>
        <StageCard n="5" title="Checks and EOBs" to="/billing/eob" testid="chain-eob"
                   score={es && es.of ? `Shadow score: ${es.agree} of ${es.of} visits paid the same as Raintree (${pct(es.pct)}); ${pct(es.dollars_pct)} of the dollars` : undefined}>
          {eob.error ? <Unavailable /> : (
            <>
              <div>{count(eob.remits)} EOBs, {count(eob.lines?.lines)} lines</div>
              <div className="text-xs text-slate-500">
                {Number(eob.lines?.open || 0)} open · {Number(eob.lines?.proposed || 0)} proposed · {Number(eob.lines?.approved || 0)} approved
              </div>
            </>
          )}
        </StageCard>
      </div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    api
      .get("/billing/home")
      .then((res) => live && setD(res.data))
      .catch(() => live && setError("Couldn't load the Billing overview."));
    return () => {
      live = false;
    };
  }, []);

  if (error) return <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</div>;
  if (!d) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  return (
    <>
      <Freshness ch={d.charges || {}} />
      <NeedsAttention d={d} />
      <Chain d={d} />
    </>
  );
}

export default function BillingHome() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="p-8 max-w-6xl" data-testid="billing-home">
      <BillingNav current="home" />
      <h1 className="text-2xl font-semibold text-slate-900">Billing</h1>
      <p className="mt-1 text-sm text-slate-500" data-testid="billing-shadow-note">
        Shadow mode. Only accounts with a billing grant can see this page, and nothing here changes Raintree.
      </p>
      <Overview />
    </div>
  );
}
